import {Binding, inputBinding, outputBinding, Type} from '@angular/core';
import {BuildingServices} from '@brunata-angular/util';
import {OptionItem} from '../shared/ui/option-card/option-card.model';
import {QuickExportMode} from '../shared/models/output-format.model';
import {FlowSlot} from '../shared/models/export-flow-slots.model';
import {FilePreviewComponent} from '../file-preview/file-preview.component';
import {FileFormatStepComponent} from '../steps/file-format-step/file-format-step.component';
import {NameStepComponent} from '../steps/name-step/name-step.component';
import {ReadingDateStepComponent} from '../steps/reading-date-step/reading-date-step.component';
import {ScheduleStepComponent} from '../steps/schedule-step/schedule-step.component';
import {UsageStepComponent} from '../steps/usage-step/usage-step.component';
import {StepBody} from '../steps/step-outlet.directive';
import {QuickExportsFormService} from './quick-exports-form.service';

export type ModeOption = OptionItem & {
    id: QuickExportMode;
    requireBuildingService?: BuildingServices;
};

export const specificDateMode: ModeOption = {
    id: 'specific-date',
    titleKey: 'quickexports_export_option_specific_date_title',
    descriptionKey: 'quickexports_export_option_specific_date_desc',
    noteKey: 'remotemeterreadings_info_message',
    noteTone: 'warning',
};

export const scheduleMode: ModeOption = {
    id: 'schedule',
    titleKey: 'quickexports_export_option_schedule_title',
    descriptionKey: 'quickexports_export_option_schedule_desc',
    requireBuildingService: BuildingServices.ASE_SAVED_EXPORTS,
};

export const quickExportModes: ReadonlyArray<ModeOption> = [specificDateMode, scheduleMode];

export type QuickSlot = Exclude<
    FlowSlot,
    'readingScopeInfo' | 'readingShape' | 'meterTypes' | 'systems' | 'columns'
>;

type SlotDefinition = {
    titleKey: string;
    render: Type<unknown>;
    bind: (form: QuickExportsFormService) => ReadonlyArray<Binding>;
};

const slotDefinitions: Record<QuickSlot, SlotDefinition> = {
    usage: {
        titleKey: 'quickexports_export_step_what_to_do_title',
        render: UsageStepComponent,
        bind: form => [
            inputBinding('options', () => form.modeOptions()),
            inputBinding('selectedId', () => form.selectedMode()),
            outputBinding<string>('optionSelected', id => form.selectModeById(id)),
        ],
    },
    readingDate: {
        titleKey: 'quickexports_export_step_reading_date_title',
        render: ReadingDateStepComponent,
        bind: form => [
            inputBinding('control', () => form.selectedDate),
            inputBinding('maxDate', () => form.maxDate),
        ],
    },
    name: {
        titleKey: 'customizedexports_export_step_name_title',
        render: NameStepComponent,
        bind: form => [inputBinding('control', () => form.form.controls.name)],
    },
    format: {
        titleKey: 'quickexports_export_step_format_title',
        render: FileFormatStepComponent,
        bind: form => [
            inputBinding('formats', () => form.formatOptions()),
            inputBinding('selectedId', () => form.selectedFormat().id),
            inputBinding('fileFormat', () => form.fileFormat),
            inputBinding('settings', () => form.fileFormatSettings()),
            outputBinding<string>('formatSelected', id => form.selectFormatById(id)),
            outputBinding<void>('settingsReset', () => form.resetFileFormatStep()),
        ],
    },
    preview: {
        titleKey: 'customizedexports_export_preview_title',
        render: FilePreviewComponent,
        bind: form => [
            inputBinding('content', () => form.previewContent()),
            inputBinding('fileName', () => form.previewFileName()),
        ],
    },
    schedule: {
        titleKey: 'quickexports_export_step_schedule_title',
        render: ScheduleStepComponent,
        bind: form => [
            inputBinding('titleKey', () => slotDefinitions.schedule.titleKey),
            inputBinding('hintKey', () => 'quickexports_export_step_schedule_hint'),
            inputBinding('schedule', () => form.form.controls.schedule),
        ],
    },
};

function isQuickSlot(slot: FlowSlot): slot is QuickSlot {
    return slot in slotDefinitions;
}

export function quickFlowSteps(
    slots: ReadonlyArray<FlowSlot>,
    form: QuickExportsFormService,
): ReadonlyArray<{id: QuickSlot; titleKey: string; body: StepBody}> {
    return slots.map(slot => {
        if (!isQuickSlot(slot)) {
            throw new Error(`The quick export flow has no step for the ${slot} slot`);
        }
        const {titleKey, render, bind} = slotDefinitions[slot];
        return {
            id: slot,
            titleKey,
            body: {
                render,
                bindings: bind(form),
            },
        };
    });
}
